import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useCharacter } from '../hooks/useCharacter'
import { useCampaign, useCampaignPlayers } from '../hooks/useCampaigns'
import { useUnitPreference } from '../hooks/useUnitPreference'
import { CampaignRoleProvider, useCampaignRole } from '../hooks/useCampaignRole'
import { useComputedStats } from '../hooks/useComputedStats'
import { useActionDispatch } from '../hooks/useActionDispatch'
import { useRealtimeCharacter } from '../hooks/useRealtimeCharacter'
import { computeEndurance, computeTotalInjuryLevels } from '../lib/computedStats'
import { IdentityPanel } from '../components/sheet/IdentityPanel'
import { AttributePanel } from '../components/sheet/AttributePanel'
import { SkillsPanel } from '../components/sheet/SkillsPanel'
import { CombatPanel } from '../components/sheet/CombatPanel'
import { InjuryTracker } from '../components/sheet/InjuryTracker'
import { FamilyPanel } from '../components/sheet/FamilyPanel'
import { ContactsPanel } from '../components/sheet/ContactsPanel'
import { HistoryPanel } from '../components/sheet/HistoryPanel'
import { PersonalityPanel } from '../components/sheet/PersonalityPanel'
import { AppearancePanel } from '../components/sheet/AppearancePanel'
import { EquipmentLoadoutPanel } from '../components/sheet/EquipmentLoadoutPanel'
import { SecretsPanel } from '../components/sheet/SecretsPanel'
import { StatusPanel } from '../components/sheet/StatusPanel'
import type { UnitPreference } from '../lib/units'

type Tab = 'profile' | 'combat' | 'skills' | 'story' | 'equipment' | 'secrets'

const TABS: { key: Tab; label: string; gmOnly?: boolean }[] = [
  { key: 'profile',   label: 'Profile' },
  { key: 'combat',    label: 'Combat' },
  { key: 'skills',    label: 'Skills' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'story',     label: 'Story' },
  { key: 'secrets',   label: 'Secrets', gmOnly: true },
]

export function CharacterSheetPage() {
  const { id } = useParams<{ id: string }>()
  const { data: character, isLoading, error } = useCharacter(id)
  useRealtimeCharacter(id)

  if (isLoading) return <div className="p-8 text-ink-light">Loading character...</div>
  if (error || !character) return <div className="p-8 text-danger">Character not found.</div>

  return (
    <CampaignRoleProvider campaignId={character.campaign_id}>
      <CharacterSheetContent character={character} id={id!} />
    </CampaignRoleProvider>
  )
}

function CharacterSheetContent({ character, id }: { character: any; id: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isGM, userId } = useCampaignRole()
  const isOwner = !!userId && character.owner_user_id === userId
  const canEdit = isGM || isOwner
  const { data: campaign } = useCampaign(character.campaign_id)
  const { pref, setPref } = useUnitPreference(character.campaign_id)
  const [showAssign, setShowAssign] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [fatigueChanging, setFatigueChanging] = useState(false)

  const { data: players = [] } = useCampaignPlayers(
    isGM && character.character_role === 'npc' ? character.campaign_id : undefined
  )

  const [tab, setTab] = useState<Tab>(() => {
    try {
      const saved = localStorage.getItem(`cs-tab-${id}`)
      return (saved as Tab) ?? 'profile'
    } catch {
      return 'profile'
    }
  })

  function handleTabChange(t: Tab) {
    setTab(t)
    try { localStorage.setItem(`cs-tab-${id}`, t) } catch { /* */ }
  }

  const currentAge = campaign ? campaign.current_tr_year - character.birth_year : 0
  const isGenerated = character.creation_method === 'automatic'
  const { up, pp, armourProtection } = useComputedStats(character)

  const endurance = computeEndurance(character)
  const totalInjury = computeTotalInjuryLevels(character.injuries ?? [])
  const fatigue = character.fatigue_levels ?? 0

  const { dispatch } = useActionDispatch(id, character.campaign_id)

  async function changeFatigue(delta: 1 | -1) {
    if (fatigueChanging) return
    setFatigueChanging(true)
    try {
      if (delta > 0) await dispatch('ADD_FATIGUE', { amount: 1 })
      else if (fatigue > 0) await dispatch('REMOVE_FATIGUE', { amount: 1 })
    } catch { /* */ }
    setFatigueChanging(false)
  }

  const visibleTabs = TABS.filter(t => !t.gmOnly || isGM)

  return (
    <div className="min-h-screen bg-parchment">

      {/* ── Sticky header ──────────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-parchment/95 backdrop-blur-sm border-b border-parchment-dark shadow-sm no-print">

        {/* Row 1: nav controls */}
        <div className="max-w-5xl mx-auto px-4 pt-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2 items-center flex-wrap">
            <button
              onClick={() => navigate(`/campaign/${character.campaign_id}`)}
              className="px-3 py-1 text-xs border border-parchment-dark rounded hover:bg-parchment-dark transition-colors">
              ← Campaign
            </button>
            <button
              onClick={() => navigate(`/character/${id}/history`)}
              className="hidden sm:inline-flex px-3 py-1 text-xs border border-parchment-dark rounded hover:bg-parchment-dark transition-colors">
              History
            </button>
            <button
              onClick={() => window.print()}
              className="hidden sm:inline-flex px-3 py-1 text-xs bg-accent text-white rounded hover:bg-accent-light transition-colors">
              Export PDF
            </button>
            {isGM && character.character_role === 'npc' && (
              <button
                onClick={() => setShowAssign(true)}
                className="px-3 py-1 text-xs border border-accent text-accent rounded hover:bg-accent hover:text-white transition-colors">
                Assign to Player
              </button>
            )}
            {isGM && character.character_role === 'pc' && character.owner_user_id && (
              <button
                onClick={async () => {
                  if (!confirm('Revoke player ownership? Character will become an NPC again.')) return
                  await supabase.from('characters').update({ character_role: 'npc', owner_user_id: null }).eq('id', id)
                  queryClient.invalidateQueries({ queryKey: ['character', id] })
                }}
                className="px-3 py-1 text-xs border border-warning text-warning rounded hover:bg-warning hover:text-white transition-colors">
                Revoke PC
              </button>
            )}
          </div>
          <div className="flex gap-1 items-center">
            <span className="hidden sm:inline text-xs text-ink-light mr-1">Units:</span>
            {(['imperial', 'metric', 'both'] as UnitPreference[]).map(p => (
              <button key={p} onClick={() => setPref(p)}
                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                  pref === p ? 'bg-accent text-white font-bold' : 'border border-parchment-dark hover:bg-parchment-dark'
                }`}>
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: character name + live combat stats */}
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
          <div className="min-w-0">
            <span className="font-cinzel font-bold text-xl text-ink leading-tight">
              {character.character_name}
            </span>
            {character.occupation && (
              <span className="text-sm text-ink-light ml-2">{character.occupation}</span>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            {/* Endurance */}
            <div className="text-center">
              <div className="text-[10px] text-ink-light uppercase tracking-wider leading-none mb-0.5">END</div>
              <div className="text-xl sm:text-2xl font-bold font-cinzel text-ink leading-none">{endurance}</div>
            </div>

            {/* Fatigue */}
            <div className="text-center">
              <div className="text-[10px] text-ink-light uppercase tracking-wider leading-none mb-0.5">Fatigue</div>
              <div className="flex items-center gap-1">
                {canEdit && (
                  <button
                    onClick={() => changeFatigue(-1)}
                    disabled={fatigueChanging || fatigue === 0}
                    className="w-7 h-7 sm:w-5 sm:h-5 flex items-center justify-center rounded border border-parchment-dark hover:bg-parchment-dark disabled:opacity-30 text-sm sm:text-xs leading-none">
                    −
                  </button>
                )}
                <span className="text-xl sm:text-2xl font-bold font-cinzel text-ink leading-none w-7 text-center">{fatigue}</span>
                {canEdit && (
                  <button
                    onClick={() => changeFatigue(1)}
                    disabled={fatigueChanging}
                    className="w-7 h-7 sm:w-5 sm:h-5 flex items-center justify-center rounded border border-parchment-dark hover:bg-parchment-dark disabled:opacity-30 text-sm sm:text-xs leading-none">
                    +
                  </button>
                )}
              </div>
            </div>

            {/* Injury */}
            <div className="text-center">
              <div className="text-[10px] text-ink-light uppercase tracking-wider leading-none mb-0.5">Injury</div>
              <div className={`text-xl sm:text-2xl font-bold font-cinzel leading-none ${totalInjury > 0 ? 'text-danger' : 'text-ink'}`}>
                {totalInjury}
              </div>
            </div>

            {/* Penalty indicator */}
            {pp > 0 ? (
              <div className="bg-danger text-white px-3 py-1 rounded font-bold text-sm">
                −{pp * 5} EML
              </div>
            ) : (
              <div className="hidden sm:block text-success text-xs border border-success/40 px-2 py-1 rounded">
                No penalty
              </div>
            )}
          </div>
        </div>

        {/* Row 3: tab bar — scrollable on mobile so no tabs are clipped */}
        <div className="max-w-5xl mx-auto px-4 flex overflow-x-auto scrollbar-none">
          {visibleTabs.map(t => (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                tab === t.key
                  ? 'border-accent text-accent'
                  : 'border-transparent text-ink-light hover:text-ink hover:border-parchment-dark'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Assign-to-player modal ─────────────────────────────────────── */}
      {showAssign && (
        <div className="max-w-5xl mx-auto px-4 mt-4 no-print">
          <div className="border border-accent rounded p-4 bg-white">
            <h4 className="font-cinzel font-semibold text-sm mb-2">Assign to Player</h4>
            {players.length === 0 ? (
              <p className="text-sm text-ink-light">No players in this campaign. Invite players first.</p>
            ) : (
              <div className="space-y-1">
                {players.map(p => (
                  <button key={p.user_id} disabled={assigning}
                    onClick={async () => {
                      setAssigning(true)
                      await supabase.from('characters').update({
                        character_role: 'pc',
                        owner_user_id: p.user_id,
                      }).eq('id', id)
                      queryClient.invalidateQueries({ queryKey: ['character', id] })
                      setShowAssign(false)
                      setAssigning(false)
                    }}
                    className="w-full text-left px-3 py-2 text-sm border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50">
                    {p.display_name || p.user_id}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowAssign(false)} className="mt-2 text-xs text-ink-light hover:text-ink">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Tab content ────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-4">

        {tab === 'profile' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <IdentityPanel character={character} currentAge={currentAge} pref={pref} isGM={isGM} />
              <StatusPanel character={character} characterId={id} campaignId={character.campaign_id} canEdit={canEdit} />
              <PersonalityPanel character={character} />
            </div>
            <div>
              <AttributePanel character={character} />
              <ContactsPanel character={character} />
            </div>
          </div>
        )}

        {tab === 'combat' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <CombatPanel
                character={character}
                physicalPenalty={pp}
                armourProtection={armourProtection}
                characterId={id}
                campaignId={character.campaign_id}
                canEdit={canEdit}
              />
            </div>
            <div>
              <InjuryTracker
                character={character}
                campaignId={character.campaign_id}
                canEdit={canEdit}
                armourProtection={armourProtection}
              />
            </div>
          </div>
        )}

        {tab === 'skills' && (
          <SkillsPanel
            character={character}
            universalPenalty={up}
            physicalPenalty={pp}
            characterId={id}
            campaignId={character.campaign_id}
            canEdit={canEdit}
          />
        )}

        {tab === 'story' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <AppearancePanel
                character={character}
                pref={pref}
                characterId={id}
                isGM={isGM}
                onRegenerate={() => queryClient.invalidateQueries({ queryKey: ['character', id] })}
              />
              <HistoryPanel character={character} />
            </div>
            <div>
              {isGenerated && (
                <FamilyPanel characterId={character.id} gameYear={campaign?.current_tr_year ?? 720} />
              )}
            </div>
          </div>
        )}

        {tab === 'equipment' && (
          <EquipmentLoadoutPanel
            character={character}
            pref={pref}
            characterId={id}
            campaignId={character.campaign_id}
            canEdit={canEdit}
          />
        )}

        {tab === 'secrets' && isGM && (
          <SecretsPanel character={character} />
        )}

      </div>
    </div>
  )
}
